<!--
StatusBadge.vue - Badge de estado reutilizable para Olvidadme
Aplicando Component Pattern + Icon Pattern de PATTERNS_FROM_BASKET_BASE.md

Contexto: Badge reutilizable con Heroicons para mostrar estados con colores semánticos
- Casos: pending, in_progress, completed, cancelled, etc.
- Documentos: uploading, processing, verified, rejected
- Pagos: pending, paid, failed, refunded

MIGRACIÓN COMPLETADA: Todos los emoticonos migrados a Heroicons profesionales
Mejora percepción de confianza para plataforma legal-tech GDPR
-->

<template>
  <span :class="badgeClasses">
    <component
      v-if="showIcon"
      :is="IconComponent"
      :class="iconClasses"
      aria-hidden="true"
    />
    {{ displayText }}
  </span>
</template>

<script setup>
import { computed } from 'vue'
import {
  // Status & State Icons
  CheckCircleIcon,
  XMarkIcon,
  ClockIcon,
  PauseIcon,
  ArrowPathIcon,
  StopIcon,
  CogIcon,
  PencilIcon,
  GlobeAltIcon,
  ArrowUpIcon,
  ArrowUturnLeftIcon,
  CreditCardIcon,
  ClipboardDocumentListIcon
} from '@heroicons/vue/24/outline'

const props = defineProps({
  status: {
    type: String,
    required: true,
    validator: (value) => [
      // Case statuses
      'pending', 'in_progress', 'completed', 'cancelled', 'on_hold',
      // Document statuses  
      'uploading', 'processing', 'processed', 'verified', 'rejected',
      // Payment statuses
      'paid', 'failed', 'refunded',
      // General statuses
      'active', 'inactive', 'draft', 'published'
    ].includes(value)
  },
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'outline', 'minimal'].includes(value)
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['xs', 'sm', 'small', 'md', 'medium', 'lg', 'large'].includes(value)
  },
  showIcon: {
    type: Boolean,
    default: true
  }
})

// Icon mapping from iconMap.js - Heroicons migration
const statusIcons = {
  // Case statuses
  pending: ClockIcon,
  in_progress: ArrowPathIcon,
  completed: CheckCircleIcon,
  cancelled: XMarkIcon,
  on_hold: PauseIcon,
  
  // Document statuses
  uploading: ArrowUpIcon,
  processing: CogIcon,
  processed: ClipboardDocumentListIcon,
  verified: CheckCircleIcon,
  rejected: XMarkIcon,
  
  // Payment statuses
  paid: CreditCardIcon,
  failed: XMarkIcon,
  refunded: ArrowUturnLeftIcon,
  
  // General statuses
  active: CheckCircleIcon,
  inactive: StopIcon,
  draft: PencilIcon,
  published: GlobeAltIcon
}

// Status configuration with semantic colors and Heroicons
const statusConfig = {
  // Case statuses
  pending: {
    color: 'yellow',
    text: 'Pendiente',
    icon: statusIcons.pending
  },
  in_progress: {
    color: 'blue',
    text: 'En progreso',
    icon: statusIcons.in_progress
  },
  completed: {
    color: 'green',
    text: 'Completado',
    icon: statusIcons.completed
  },
  cancelled: {
    color: 'red',
    text: 'Cancelado',
    icon: statusIcons.cancelled
  },
  on_hold: {
    color: 'orange',
    text: 'En espera',
    icon: statusIcons.on_hold
  },
  
  // Document statuses
  uploading: {
    color: 'blue',
    text: 'Subiendo',
    icon: statusIcons.uploading
  },
  processing: {
    color: 'yellow',
    text: 'Procesando',
    icon: statusIcons.processing
  },
  processed: {
    color: 'blue',
    text: 'Procesado',
    icon: statusIcons.processed
  },
  verified: {
    color: 'green',
    text: 'Verificado',
    icon: statusIcons.verified
  },
  rejected: {
    color: 'red',
    text: 'Rechazado',
    icon: statusIcons.rejected
  },
  
  // Payment statuses
  paid: {
    color: 'green',
    text: 'Pagado',
    icon: statusIcons.paid
  },
  failed: {
    color: 'red',
    text: 'Falló',
    icon: statusIcons.failed
  },
  refunded: {
    color: 'orange',
    text: 'Reembolsado',
    icon: statusIcons.refunded
  },
  
  // General statuses
  active: {
    color: 'green',
    text: 'Activo',
    icon: statusIcons.active
  },
  inactive: {
    color: 'gray',
    text: 'Inactivo',
    icon: statusIcons.inactive
  },
  draft: {
    color: 'gray',
    text: 'Borrador',
    icon: statusIcons.draft
  },
  published: {
    color: 'green',
    text: 'Publicado',
    icon: statusIcons.published
  }
}

const currentConfig = computed(() => statusConfig[props.status])

const displayText = computed(() => currentConfig.value?.text || props.status)

const IconComponent = computed(() => currentConfig.value?.icon || CheckCircleIcon)

// Color classes based on status
const colorClasses = computed(() => {
  const color = currentConfig.value?.color || 'gray'
  const variant = props.variant
  
  const colorMap = {
    green: {
      default: 'bg-green-100 text-green-800 border-green-200',
      outline: 'border-green-300 text-green-700 bg-transparent',
      minimal: 'text-green-700'
    },
    blue: {
      default: 'bg-blue-100 text-blue-800 border-blue-200',
      outline: 'border-blue-300 text-blue-700 bg-transparent',
      minimal: 'text-blue-700'
    },
    yellow: {
      default: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      outline: 'border-yellow-300 text-yellow-700 bg-transparent',
      minimal: 'text-yellow-700'
    },
    orange: {
      default: 'bg-orange-100 text-orange-800 border-orange-200',
      outline: 'border-orange-300 text-orange-700 bg-transparent',
      minimal: 'text-orange-700'
    },
    red: {
      default: 'bg-red-100 text-red-800 border-red-200',
      outline: 'border-red-300 text-red-700 bg-transparent',
      minimal: 'text-red-700'
    },
    gray: {
      default: 'bg-gray-100 text-gray-800 border-gray-200',
      outline: 'border-gray-300 text-gray-700 bg-transparent',
      minimal: 'text-gray-700'
    }
  }
  
  return colorMap[color]?.[variant] || colorMap.gray[variant]
})

// Size classes (supports both Tailwind convention xs/sm/md/lg and verbose small/medium/large)
const sizeClasses = computed(() => {
  const sizeMap = {
    xs: 'px-1.5 py-0.5 text-xs',
    sm: 'px-2 py-1 text-xs',
    small: 'px-2 py-1 text-xs',
    md: 'px-3 py-1 text-sm',
    medium: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
    large: 'px-4 py-2 text-base'
  }

  return sizeMap[props.size] || sizeMap.medium
})

// Combined badge classes
const badgeClasses = computed(() => {
  const baseClasses = 'inline-flex items-center font-medium rounded-full border'
  const variantClasses = props.variant !== 'minimal' ? 'border' : ''
  
  return [
    baseClasses,
    variantClasses,
    colorClasses.value,
    sizeClasses.value
  ].filter(Boolean).join(' ')
})

// Icon classes with responsive sizes (supports both Tailwind convention and verbose)
const iconClasses = computed(() => {
  const sizeMap = {
    xs: 'w-2.5 h-2.5 mr-0.5',
    sm: 'w-3 h-3 mr-1',
    small: 'w-3 h-3 mr-1',
    md: 'w-4 h-4 mr-1.5',
    medium: 'w-4 h-4 mr-1.5',
    lg: 'w-5 h-5 mr-2',
    large: 'w-5 h-5 mr-2'
  }

  return sizeMap[props.size] || sizeMap.medium
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Custom hover effects for interactive badges */
.badge-interactive {
  @apply cursor-pointer transition-colors duration-200;
}

.badge-interactive:hover {
  @apply opacity-80;
}

/* Accessibility improvements */
.badge-focus {
  @apply focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500;
}
</style>
